import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, merge, Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";

/**
 * FLIP-321. Answers "is something blocking right now" for the coach-mark engine (FLIP-303),
 * so a coach-mark never surfaces underneath or on top of an active dialog/snackbar.
 *
 * Spike finding: no new modal/snackbar registry is needed. `MatDialog` already tracks every
 * open dialog app-wide via its own `providedIn: 'root'` singleton (confirmed working precedent:
 * `version-check.service.ts`'s `openDialogs.length > 0` check), so this service reads that
 * directly via `afterOpened()`/`afterAllClosed()` rather than duplicating it. `MatSnackBar`
 * already guarantees only one snackbar visible at a time, but exposes no public "is one open"
 * signal - `setSnackbarOpen()` below is that one missing piece, fed by `SnackbarService`.
 *
 * Known gap: 14 call sites across the app inject `MatSnackBar` directly instead of going
 * through `SnackbarService`, so snackbars opened from those sites won't register here. Fixing
 * those call sites is out of scope for this spike - flagged as a follow-up for FLIP-303.
 *
 * Open question (not resolvable from this repo): the app runs inside both a Flutter and a
 * React Native WebView shell (both in sibling repos). No evidence was found of either native
 * shell presenting its own non-Material overlay on top of the Angular app, but that can't be
 * fully ruled out here - if FLIP-303 sees coach-marks appearing under a native overlay, that's
 * the first place to look.
 *
 * Pending checks (FLIP-334): some product modals (the community promo, the update-complete
 * modal) are decided by an async fetch at boot, so for a moment nothing is open yet but one is
 * about to be. `registerPendingCheck()` counts those windows as blocking, so onboarding waits for
 * the modal (or the decision not to show one) instead of opening first and being stacked on.
 */
@Injectable({ providedIn: "root" })
export class BlockingUiSignalService {
  private readonly snackbarOpenSub = new BehaviorSubject<boolean>(false);
  private readonly pendingChecksSub = new BehaviorSubject<number>(0);

  readonly isBlockingUiActive$: Observable<boolean>;

  constructor(private readonly matDialog: MatDialog) {
    const dialogOpen$ = merge(
      this.matDialog.afterOpened,
      this.matDialog.afterAllClosed
    ).pipe(
      startWith(null),
      map(() => this.matDialog.openDialogs.length > 0)
    );

    this.isBlockingUiActive$ = merge(dialogOpen$, this.snackbarOpenSub, this.pendingChecksSub).pipe(
      map(
        () =>
          this.matDialog.openDialogs.length > 0 ||
          this.snackbarOpenSub.value ||
          this.pendingChecksSub.value > 0
      )
    );
  }

  /**
   * Marks the start of an async "should a blocking modal open?" check; call the returned function
   * once it settles (after opening the modal, or deciding not to). Safe to call more than once.
   */
  registerPendingCheck(): () => void {
    this.pendingChecksSub.next(this.pendingChecksSub.value + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.pendingChecksSub.next(this.pendingChecksSub.value - 1);
    };
  }

  /** Called by `SnackbarService` around each snackbar's open/dismiss lifecycle. */
  setSnackbarOpen(isOpen: boolean): void {
    this.snackbarOpenSub.next(isOpen);
  }
}
